/**
 * @fileoverview ลูกค้าที่กำลังถูกเปิดดูอยู่ในพอร์ทัลลูกค้า (/cs/*)
 *
 * ผู้ใช้ที่เป็นลูกค้าเองผูกกับลูกค้าของตัวเองจาก token อยู่แล้ว ไม่ต้องเลือกอะไร
 * ส่วนพนักงานภายใน (แอดมิน/ซูเปอร์ยูเซอร์) ต้องเลือกลูกค้าจาก modal ก่อน แล้วค่านั้น
 * จะถูกส่งไปกับทุก request เป็น header `customer-uid` (ดู services/api.js) ซึ่ง
 * auth.middleware ฝั่ง backend จะแปลงเป็น res.customer_id ให้เอง
 *
 * @module utils/customerScope
 */

import { getItem, setItem, removeItem } from './localStorage';

const STORAGE_KEY = 'cm-cs-customer';

/** user_type ที่ผูกกับลูกค้ารายเดียวอยู่แล้ว/เข้าพอร์ทัลนี้ไม่ได้ - ไม่ต้องเลือกลูกค้า */
const NON_STAFF_USER_TYPES = ['customer', 'technician'];

/**
 * ผู้ใช้คนนี้เป็นพนักงานภายในที่ต้องเลือกลูกค้าก่อนใช้พอร์ทัลลูกค้าหรือไม่
 * @param {Object|null} profile - cm-profile ใน localStorage
 * @returns {boolean}
 */
export const isStaffViewingCustomerPortal = (profile) => {
  const userType = profile?.user_type || '';
  if (!userType) return false;
  return !NON_STAFF_USER_TYPES.includes(userType);
};

/**
 * uid ของบริษัทที่กำลังใช้งานอยู่ (cm-company) - ผูกไว้กับลูกค้าที่เลือก
 * เพราะลูกค้ารายหนึ่งอยู่ใต้บริษัทเดียว ถ้าสลับบริษัทแล้วยังส่ง customer-uid เดิม backend จะปฏิเสธทุก request
 * @returns {string}
 */
const getActiveCompanyUid = () => getItem('cm-company', null)?.id || '';

/**
 * ลูกค้าที่เลือกไว้ล่าสุด (ถ้าสลับบริษัทไปแล้วถือว่ายังไม่ได้เลือก และล้างค่าเก่าทิ้ง)
 * @returns {{uid: string, name: string, customer_code: string, company_uid: string}|null}
 */
export const getSelectedCustomer = () => {
  const selected = getItem(STORAGE_KEY, null);
  if (!selected?.uid) return null;

  const companyUid = getActiveCompanyUid();
  if (selected.company_uid && companyUid && selected.company_uid !== companyUid) {
    removeItem(STORAGE_KEY);
    return null;
  }

  return selected;
};

/**
 * uid ของลูกค้าที่เลือกไว้ (ใช้เป็น header customer-uid)
 * @returns {string}
 */
export const getSelectedCustomerUid = () => getSelectedCustomer()?.uid || '';

/**
 * บันทึกลูกค้าที่เลือกจาก modal
 * @param {{uid: string, name: string, customer_code: string}} customer
 */
export const setSelectedCustomer = (customer) =>
  setItem(STORAGE_KEY, { ...customer, company_uid: getActiveCompanyUid() });

/** ล้างลูกค้าที่เลือกไว้ (ตอนกดสลับลูกค้า / ออกจากพอร์ทัล) */
export const clearSelectedCustomer = () => removeItem(STORAGE_KEY);

/**
 * พร้อมยิง API ของพอร์ทัลลูกค้าหรือยัง
 * พนักงานที่ยังไม่ได้เลือกลูกค้าต้องไม่ยิง API (navbar จะเด้ง modal ให้เลือกก่อน)
 * ไม่งั้นทุกหน้าจะขึ้น error "customer-uid is required" ตั้งแต่เปิดเข้ามา
 * @param {Object|null} profile - cm-profile ใน localStorage
 * @returns {boolean}
 */
export const isCustomerScopeReady = (profile) => {
  if (!isStaffViewingCustomerPortal(profile)) return true;
  return Boolean(getSelectedCustomerUid());
};

const customerScope = {
  isStaffViewingCustomerPortal,
  isCustomerScopeReady,
  getSelectedCustomer,
  getSelectedCustomerUid,
  setSelectedCustomer,
  clearSelectedCustomer,
};

export default customerScope;
